<script lang="ts">
	import Footer from '$lib/Footer.svelte';
	import Header from '$lib/Header.svelte';
	import Icon from '$lib/Icon.svelte';

	const capabilities = [
		{
			icon: 'message' as const,
			title: 'Чаты и файлы',
			text: 'Обсуждайте планы, делитесь файлами и собирайте близких или коллег в одном чате.'
		},
		{
			icon: 'phone' as const,
			title: 'Аудио и видео',
			text: 'Звоните один на один или всей группой. Включайте камеру и показывайте свой экран.'
		},
		{
			icon: 'mail' as const,
			title: 'Почта рядом',
			text: 'Читайте письма и отвечайте на них рядом с чатами. Свой адрес @andresot.ru — после подтверждения аккаунта.'
		},
		{
			icon: 'story' as const,
			title: 'Истории',
			text: 'Делитесь моментами в фото, видео или тексте. Выбирайте, кому будут видны ваши истории.'
		}
	];

	const faq = [
		{
			question: 'Нужно ли устанавливать приложение?',
			answer:
				'Нет. ANDRESOT работает прямо в браузере. При желании его можно установить как PWA — с отдельным окном и значком на устройстве.'
		},
		{
			question: 'Для регистрации нужен номер телефона?',
			answer:
				'Нет. Достаточно выбрать логин, указать имя и подтвердить внешний адрес электронной почты.'
		},
		{
			question: 'Где работает ANDRESOT?',
			answer:
				'В современных браузерах на телефонах и компьютерах. Установка поддерживается на iPhone, iPad, Android, Windows, macOS и Linux.'
		},
		{
			question: 'Как связаться с поддержкой?',
			answer: 'Напишите на support@andresot.ru или откройте раздел «Помощь» внутри приложения.'
		}
	];
</script>

<svelte:head>
	<title>ANDRESOT — чаты, звонки, файлы и почта</title>
	<meta
		name="description"
		content="ANDRESOT — самостоятельный сервис с чатами, аудио- и видеозвонками, историями, файлами и встроенной почтой. Работает в браузере и как PWA."
	/>
	<link rel="canonical" href="https://andresot.ru/" />
	<meta property="og:type" content="website" />
	<meta property="og:site_name" content="ANDRESOT" />
	<meta property="og:locale" content="ru_RU" />
	<meta property="og:url" content="https://andresot.ru/" />
	<meta property="og:title" content="ANDRESOT — связь без переключений" />
	<meta
		property="og:description"
		content="Чаты, звонки, файлы, истории и почта — в одном самостоятельном сервисе."
	/>
	<meta property="og:image" content="https://andresot.ru/og-andresot.png" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content="ANDRESOT — связь без переключений" />
	<meta
		name="twitter:description"
		content="Чаты, звонки, файлы, истории и почта — в одном сервисе."
	/>
	<meta name="twitter:image" content="https://andresot.ru/og-andresot.png" />
</svelte:head>

<Header />

<main id="main-content">
	<section class="page hero">
		<div class="hero-copy">
			<div class="eyebrow">Ближе друг к другу</div>
			<h1>
				Чаты, звонки,<br /> файлы и почта —<br /> <span class="accent-text">в одном ANDRESOT</span>
			</h1>
			<p>
				Общайтесь с близкими и работайте вместе. В браузере, на телефоне и компьютере — с одним
				аккаунтом.
			</p>
			<div class="hero-actions">
				<a class="button" href="https://web.andresot.ru">
					Открыть ANDRESOT <Icon name="arrow" size={19} />
				</a>
				<a class="button secondary" href="/download/">Установить приложение</a>
			</div>
			<div class="hero-proof" aria-label="Ключевые особенности">
				<span><Icon name="check" size={16} /> Регистрация без номера телефона</span>
				<span><Icon name="check" size={16} /> Установка из браузера</span>
			</div>
		</div>

		<div class="hero-visual" aria-label="Интерфейс ANDRESOT на компьютере">
			<div class="product-frame desktop-frame">
				<picture>
					<source srcset="/product/andresot-desktop.webp" type="image/webp" />
					<img
						src="/product/andresot-desktop.png"
						alt="Открытый групповой чат ANDRESOT с демонстрационными сообщениями"
						width="1280"
						height="800"
						fetchpriority="high"
					/>
				</picture>
			</div>
			<p class="image-caption">Ваши разговоры. Ваше пространство.</p>
		</div>
	</section>

	<section class="page section" id="product">
		<div class="section-kicker">Возможности</div>
		<div class="section-heading split-heading">
			<h2>Всё для общения.<br />Рядом друг с другом.</h2>
			<p>
				От короткого «как дела?» до совместных планов. Выбирайте удобный способ оставаться на связи.
			</p>
		</div>

		<div class="capability-grid">
			{#each capabilities as capability (capability.title)}
				<article class="capability-card">
					<div class="icon-box"><Icon name={capability.icon} size={24} /></div>
					<h3>{capability.title}</h3>
					<p>{capability.text}</p>
				</article>
			{/each}
		</div>

		<div class="product-tour">
			<article class="tour-row">
				<div class="tour-copy">
					<span class="tour-index">01 / общение</span>
					<h3>Переписка остаётся в контексте</h3>
					<p>
						Обсуждайте важное в личных диалогах и группах. Отвечайте на конкретные сообщения,
						собирайте мнения в опросах и находите нужные файлы в переписке.
					</p>
					<ul class="check-list">
						<li><Icon name="check" size={16} /> Группы, каналы и личные диалоги</li>
						<li><Icon name="check" size={16} /> Файлы, реакции, ответы и опросы</li>
					</ul>
				</div>
				<div class="tour-shot wide-shot">
					<picture>
						<source srcset="/product/andresot-desktop.webp" type="image/webp" />
						<img
							src="/product/andresot-desktop.png"
							alt="Групповой чат ANDRESOT"
							width="1280"
							height="800"
							loading="lazy"
						/>
					</picture>
				</div>
			</article>

			<article class="tour-row reverse">
				<div class="tour-copy">
					<span class="tour-index">02 / звонки</span>
					<h3>Когда хочется услышать голос</h3>
					<p>
						Позвоните прямо из чата. Соберите друзей в групповом разговоре или покажите коллегам
						экран, чтобы быстрее обсудить идею.
					</p>
					<ul class="check-list">
						<li><Icon name="check" size={16} /> Сквозное шифрование звука и видео звонков</li>
						<li><Icon name="check" size={16} /> Личные и групповые звонки</li>
					</ul>
				</div>
				<div class="tour-shot phone-shot">
					<picture>
						<source srcset="/product/andresot-call.webp" type="image/webp" />
						<img
							src="/product/andresot-call.png"
							alt="Раздел звонков ANDRESOT на телефоне"
							width="390"
							height="844"
							loading="lazy"
						/>
					</picture>
				</div>
			</article>

			<article class="tour-row">
				<div class="tour-copy">
					<span class="tour-index">03 / почта</span>
					<h3>Почта не требует отдельного приложения</h3>
					<p>
						Ящик @andresot.ru открывается рядом с чатами. Доступны папки, поиск, вложения, ответ и
						пересылка.
					</p>
					<ul class="check-list">
						<li><Icon name="check" size={16} /> Адрес после подтверждения аккаунта</li>
						<li><Icon name="check" size={16} /> Пароли для внешних почтовых клиентов</li>
					</ul>
				</div>
				<div class="tour-shot mail-shot">
					<picture>
						<source srcset="/product/andresot-mail.webp" type="image/webp" />
						<img
							src="/product/andresot-mail.png"
							alt="Встроенная почта ANDRESOT"
							width="1280"
							height="800"
							loading="lazy"
						/>
					</picture>
				</div>
			</article>
		</div>
	</section>

	<section class="technology-section" id="technology">
		<div class="page section">
			<div class="section-kicker">Приватность и устройства</div>
			<div class="section-heading split-heading">
				<h2>Общение на ваших условиях</h2>
				<p>
					Выбирайте, с кем делиться моментами, и управляйте доступом к аккаунту. Продолжайте общение
					там, где вам удобно.
				</p>
			</div>

			<div class="trust-grid">
				<article>
					<Icon name="shield" size={24} />
					<h3>Вы выбираете аудиторию</h3>
					<p>
						Настройте видимость профиля и историй. Ограничьте нежелательные сообщения с помощью
						блокировки.
					</p>
				</article>
				<article>
					<Icon name="lock" size={24} />
					<h3>Устройства под контролем</h3>
					<p>
						Проверяйте активные сеансы в настройках. Завершите доступ на устройстве, которым больше
						не пользуетесь.
					</p>
				</article>
				<article>
					<Icon name="monitor" size={24} />
					<h3>С телефона на компьютер</h3>
					<p>Войдите в тот же аккаунт на другом устройстве. Ваши чаты и почта будут рядом.</p>
				</article>
			</div>
		</div>
	</section>

	<section class="page section" id="faq">
		<div class="section-kicker">Коротко о главном</div>
		<div class="section-heading"><h2>Вопросы перед началом</h2></div>
		<div class="faq-list">
			{#each faq as item (item.question)}
				<details>
					<summary>{item.question}<span aria-hidden="true"></span></summary>
					<p>{item.answer}</p>
				</details>
			{/each}
		</div>
	</section>

	<section class="page cta-section">
		<div class="section-kicker">Будем на связи</div>
		<h2>Начните с одного сообщения</h2>
		<p>Откройте приложение в браузере. Установить его на устройство можно в любой момент.</p>
		<div class="hero-actions">
			<a class="button" href="https://web.andresot.ru"
				>Открыть ANDRESOT <Icon name="arrow" size={19} /></a
			>
			<a class="button secondary" href="mailto:support@andresot.ru">Задать вопрос</a>
		</div>
	</section>
</main>

<Footer />
